import { useEffect } from 'react'
import {
  keepPreviousData,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
  type Query,
  type QueryClient,
  type QueryKey,
  type UseQueryOptions,
} from '@tanstack/react-query'
import { AxiosError } from 'axios'
import {
  bulkWavePlanDcSplits,
  bulkWavePlanLines,
  createWavePlan,
  createWavePlanLineComment,
  decideWavePlanSubmission,
  deleteWavePlanComment,
  fetchWavePlan,
  fetchWavePlanDc,
  fetchWavePlanLineComments,
  fetchWavePlanLines,
  fetchWavePlanParameters,
  fetchWavePlanRollup,
  fetchWavePlans,
  fetchWavePlanUndoStack,
  fetchWavePlanWeeks,
  previewWavePlanRefresh,
  refreshWavePlan,
  resetWavePlanDcSplits,
  resetWavePlanLine,
  resetWavePlanLines,
  resetWavePlanWeeks,
  submitWavePlan,
  undoWavePlanChange,
  updateWavePlanDcSplit,
  updateWavePlanLineWeeks,
  updateWavePlanCoreReceipts,
  updateWavePlanWeeks,
  withdrawWavePlanSubmission,
  type WavePlanDcBulkRequest,
  type WavePlanDcResponse,
  type WavePlanDecision,
  type WavePlanLinesResponse,
  type WavePlanListParams,
  type WavePlanSkuBulkRequest,
  type WavePlanSkuBulkResponse,
  type WavePlanWeeksResponse,
} from '@/api/wavePlans'
import {
  dcSplitWithEdit,
  skuRequestUnchanged,
  skuWeeksWithEdit,
} from '@/components/WavePlanning/editRequests'
import { samePercent } from '@/components/WavePlanning/model'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'

const keys = queryKeys.wavePlans

/** The user's undo history of a plan (under the plan, so every plan edit refreshes it). */
const undoStackKey = (id: string) => [...keys.plan(id), 'undo-stack'] as const

/** Mutations of one plan (``useIsMutating`` tells when any is running). */
const planMutationKey = (id: string) => ['wave-plan-mutation', id] as const

const problemType = (error: unknown) =>
  error instanceof AxiosError ? String(error.response?.data?.type ?? '') : ''

/** A 409 from a stale If-Match: the plan changed elsewhere. */
export function isWavePlanConflict(error: unknown): boolean {
  return (
    error instanceof AxiosError &&
    error.response?.status === 409 &&
    /wave-plan-version-mismatch$/.test(problemType(error))
  )
}

/** The plan became read-only elsewhere (submitted, approved, AP frozen). */
const isWavePlanLocked = (error: unknown) =>
  /wave-plan-locked$/.test(problemType(error))

/** HTTP status of a failed request (undefined for network errors). */
export const errorStatus = (error: unknown) =>
  error instanceof AxiosError ? error.response?.status : undefined

/**
 * Every edit invalidates the other views of the same plan: they were computed
 * from an older inputs_version, so their (now stale) data must not be edited.
 * ``keep`` spares the queries the mutation already updated from its response.
 */
function invalidatePlan(
  client: QueryClient,
  id: string,
  keep?: (query: Query) => boolean
) {
  void client.invalidateQueries({
    queryKey: keys.plan(id),
    predicate: query => !keep?.(query) && !buildsRequests(id, query),
  })
  void client.invalidateQueries({ queryKey: keys.lists })
  void client.invalidateQueries({ queryKey: keys.rollups })
  // Awaited by mutations: the views the next edit builds its request from.
  return client.invalidateQueries({
    queryKey: keys.plan(id),
    predicate: query => !keep?.(query) && buildsRequests(id, query),
  })
}

/** The SKU, overall and DC views (edits build their requests from them). */
function buildsRequests(id: string, query: Query): boolean {
  const [, plan, view] = query.queryKey as unknown[]
  return plan === id && ['lines', 'weeks', 'dc'].includes(String(view))
}

/** A query loaded, idle and not invalidated: its data is current. */
function isFresh(client: QueryClient, key: QueryKey): boolean {
  const state = client.getQueryState(key)
  return (
    !!state &&
    state.status === 'success' &&
    state.fetchStatus === 'idle' &&
    !state.isInvalidated
  )
}

const sameKey = (key: QueryKey) => (query: Query) =>
  JSON.stringify(query.queryKey) === JSON.stringify(key)

const STALE_TOAST_MS = 6000

function onWaveError(client: QueryClient, id: string, fallback: string) {
  return (error: unknown) => {
    const locked = isWavePlanLocked(error)
    if (locked || isWavePlanConflict(error)) {
      toast.warning(
        locked
          ? `${getErrorMessage(error, fallback)} Your change was not saved.`
          : 'The wave plan changed elsewhere, so your change was not saved. The latest version is loaded.',
        STALE_TOAST_MS
      )
      void invalidatePlan(client, id)
      return
    }
    toast.error(getErrorMessage(error, fallback))
  }
}

/**
 * A mutation of one wave plan: invalidates the plan's views on success (unless
 * ``onSuccess`` updates the cache itself) and toasts errors. A plan's mutations
 * share a scope, so they run one at a time in the order they were made, and the
 * next one starts only after this one's views are refetched (``onSuccess`` is
 * awaited): it then builds its request from fresh cells and the fresh version.
 */
function usePlanMutation<TVariables, TData>(
  id: string,
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: {
    error: string
    success?: string | ((variables: TVariables) => string)
    onSuccess?: (
      client: QueryClient,
      data: TData,
      variables: TVariables
    ) => void | Promise<unknown>
    onError?: (client: QueryClient, error: unknown) => void
  }
) {
  const client = useQueryClient()
  return useMutation({
    mutationFn,
    mutationKey: planMutationKey(id),
    scope: { id: `wave-plan:${id}` },
    onSuccess: async (data, variables) => {
      const message =
        typeof options.success === 'function'
          ? options.success(variables)
          : options.success
      if (message) toast.success(message)
      if (options.onSuccess) await options.onSuccess(client, data, variables)
      else await invalidatePlan(client, id)
    },
    onError: (error: unknown) => {
      onWaveError(client, id, options.error)(error)
      options.onError?.(client, error)
    },
  })
}

/** Whether any edit of the plan is running or queued. */
export function useWavePlanMutating(id: string): boolean {
  return useIsMutating({ mutationKey: planMutationKey(id) }) > 0
}

/** Retry server and network failures once; a 4xx answer will not change. */
const retryServerErrors = (failureCount: number, error: unknown) => {
  const status = errorStatus(error)
  return failureCount < 1 && (status === undefined || status >= 500)
}

const toastedErrors = new Set<string>()
const lastToastAt = new Map<string, number>()
const SAME_TOAST_WINDOW_MS = 2000

/**
 * ``useQuery`` that toasts each failure once: several components may share a
 * query, and a page's queries often fail together with the same message (a
 * missing plan fails its detail and its weeks).
 */
function useWaveQuery<TData>(
  options: UseQueryOptions<TData, Error, TData, QueryKey> & {
    errorMessage: string
  }
) {
  const { errorMessage, ...queryOptions } = options
  const query = useQuery({ retry: retryServerErrors, ...queryOptions })
  const key = `${JSON.stringify(queryOptions.queryKey)}:${query.errorUpdatedAt}`
  useEffect(() => {
    if (!query.error || toastedErrors.has(key)) return
    toastedErrors.add(key)
    const message = getErrorMessage(query.error, errorMessage)
    const now = Date.now()
    if (now - (lastToastAt.get(message) ?? 0) < SAME_TOAST_WINDOW_MS) return
    lastToastAt.set(message, now)
    toast.error(message)
  }, [query.error, key, errorMessage])
  return query
}

// -----------------------------------------------------------------------------
// Queries
// -----------------------------------------------------------------------------

export function useWavePlanList(params: WavePlanListParams) {
  return useWaveQuery({
    queryKey: keys.list(params),
    queryFn: () => fetchWavePlans(params),
    placeholderData: keepPreviousData,
    errorMessage: 'Could not load the wave plans',
  })
}

export function useWavePlan(id: string | undefined) {
  return useWaveQuery({
    queryKey: keys.detail(id ?? ''),
    queryFn: () => fetchWavePlan(id!),
    enabled: !!id,
    errorMessage: 'Could not load the wave plan',
  })
}

export function useWavePlanWeeks(id: string | undefined) {
  return useWaveQuery({
    queryKey: keys.weeks(id ?? ''),
    queryFn: () => fetchWavePlanWeeks(id!),
    enabled: !!id,
    errorMessage: 'Could not load the overall plan',
  })
}

export function useWavePlanLines(id: string | undefined) {
  return useWaveQuery({
    queryKey: keys.lines(id ?? ''),
    queryFn: () => fetchWavePlanLines(id!),
    enabled: !!id,
    errorMessage: 'Could not load the SKU detail',
  })
}

/** Active calculation parameters (the information drawer). The key sits under
 * the plan's prefix, so plan-wide invalidations refresh it too. */
export function useWavePlanParameters(id: string | undefined) {
  return useWaveQuery({
    queryKey: [...keys.plan(id ?? ''), 'parameters'],
    queryFn: () => fetchWavePlanParameters(id!),
    enabled: !!id,
    errorMessage: 'Could not load the calculation parameters',
  })
}

export function useWavePlanDc(id: string | undefined, weekId: string | null) {
  return useWaveQuery({
    queryKey: keys.dc(id ?? '', weekId),
    queryFn: () => fetchWavePlanDc(id!, weekId),
    enabled: !!id,
    placeholderData: keepPreviousData,
    errorMessage: 'Could not load the distribution centers',
  })
}

export function useWavePlanRollup(ids: string[]) {
  return useWaveQuery({
    queryKey: keys.rollup(ids),
    queryFn: () => fetchWavePlanRollup(ids),
    enabled: ids.length > 0,
    errorMessage: 'Could not load the rollup',
  })
}

export function useWavePlanRefreshPreview(id: string, enabled: boolean) {
  return useWaveQuery({
    queryKey: keys.refreshPreview(id),
    queryFn: () => previewWavePlanRefresh(id),
    enabled,
    staleTime: 0,
    errorMessage: 'Could not compare with the assortment plan',
  })
}

export function useWavePlanLineComments(id: string, lineRefId: string | null) {
  return useWaveQuery({
    errorMessage: 'Could not load the comments',
    queryKey: keys.lineComments(id, lineRefId ?? ''),
    queryFn: () => fetchWavePlanLineComments(id, lineRefId!),
    enabled: !!lineRefId,
  })
}

// -----------------------------------------------------------------------------
// Mutations
// -----------------------------------------------------------------------------

export function useCreateWavePlan() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: createWavePlan,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.all }),
    onError: (error: unknown) =>
      toast.error(getErrorMessage(error, 'Could not start the wave plan')),
  })
}

/**
 * A view requests are built from. An invalidated one (its refetch failed or
 * hasn't finished) is refused: an edit built from it would replace newer inputs
 * or fail on a stale version.
 */
function cachedView<T>(client: QueryClient, key: QueryKey, what: string): T {
  const data = client.getQueryData<T>(key)
  if (!data) throw new Error(`The ${what} is not loaded yet. Try again.`)
  if (client.getQueryState(key)?.isInvalidated)
    throw new Error(`The ${what} is not up to date yet. Try again.`)
  return data
}

/** The freshest cached DC view showing a week (requested by id or as the first wave). */
function cachedDcView(
  client: QueryClient,
  id: string,
  weekId: string
): WavePlanDcResponse {
  const views = client
    .getQueryCache()
    .findAll({ queryKey: keys.dcViews(id) })
    .filter(query => !query.state.isInvalidated)
    .map(query => query.state.data as WavePlanDcResponse | undefined)
    .filter((view): view is WavePlanDcResponse => view?.week?.id === weekId)
  if (!views.length)
    throw new Error(
      'The distribution centers are not loaded or not up to date yet. Try again.'
    )
  return views.reduce((best, view) =>
    view.inputs_version > best.inputs_version ? view : best
  )
}

/** The newest inputs version any up-to-date cached view of the plan has seen. */
function latestVersion(client: QueryClient, id: string): number {
  const versions = client
    .getQueryCache()
    .findAll({ queryKey: keys.plan(id) })
    .filter(query => !query.state.isInvalidated)
    .map(
      query =>
        (query.state.data as { inputs_version?: number } | undefined)
          ?.inputs_version
    )
    .filter((version): version is number => typeof version === 'number')
  if (!versions.length)
    throw new Error(
      'The wave plan is not loaded or not up to date yet. Try again.'
    )
  return Math.max(...versions)
}

// Single-cell edits run in the plan's mutation scope and build their request
// when they execute, from the view they edit: a quick second edit then includes
// the first one and carries the version its response returned.

export function useUpdateWavePlanWeeks(id: string) {
  const client = useQueryClient()
  return usePlanMutation(
    id,
    ({ weekId, percent }: { weekId: string; percent: number }) => {
      const view = cachedView<WavePlanWeeksResponse>(
        client,
        keys.weeks(id),
        'overall plan'
      )
      return updateWavePlanWeeks(
        id,
        [{ week_id: weekId, default_percent: percent }],
        view.inputs_version
      )
    },
    {
      error: 'Could not update the wave plan',
      onSuccess: (client, data) => {
        client.setQueryData(keys.weeks(id), data)
        return invalidatePlan(client, id, sameKey(keys.weeks(id)))
      },
    }
  )
}

export function useUpdateWavePlanCoreReceipts(id: string) {
  const client = useQueryClient()
  return usePlanMutation(
    id,
    (receipts: {
      initial_week_id: string | null
      backup_week_id: string | null
    }) => updateWavePlanCoreReceipts(id, receipts, latestVersion(client, id)),
    {
      error: 'Could not change the receipt weeks',
      onSuccess: (client, data) => {
        client.setQueryData(keys.weeks(id), data)
        return invalidatePlan(client, id, sameKey(keys.weeks(id)))
      },
    }
  )
}

export function useResetWavePlanWeeks(id: string) {
  const client = useQueryClient()
  return usePlanMutation(
    id,
    () => resetWavePlanWeeks(id, latestVersion(client, id)),
    {
      error: 'Could not reset the distribution',
      success: 'Overall distribution reset',
      onSuccess: (client, data) => {
        client.setQueryData(keys.weeks(id), data)
        return invalidatePlan(client, id, sameKey(keys.weeks(id)))
      },
    }
  )
}

export function useUpdateWavePlanLine(id: string) {
  const client = useQueryClient()
  return usePlanMutation(
    id,
    ({
      lineRefId,
      weekId,
      percent,
    }: {
      lineRefId: string
      weekId: string
      percent: number
    }) => {
      const view = cachedView<WavePlanLinesResponse>(
        client,
        keys.lines(id),
        'SKU detail'
      )
      const weeks = skuWeeksWithEdit(view, lineRefId, weekId, percent)
      // Nothing changes (e.g. the cell retyped as it was): nothing to save.
      if (skuRequestUnchanged(view, lineRefId, weeks))
        return Promise.resolve(null)
      return updateWavePlanLineWeeks(id, lineRefId, weeks, view.inputs_version)
    },
    {
      error: 'Could not update the SKU',
      onSuccess: (client, data, variables) => {
        if (!data) return
        // The response holds the edited line plus fresh totals and visible weeks.
        client.setQueryData<WavePlanLinesResponse>(keys.lines(id), current =>
          current
            ? {
                ...current,
                inputs_version: data.inputs_version,
                weeks: data.weeks,
                totals: data.totals,
                lines: current.lines.map(line =>
                  line.line_ref_id === variables.lineRefId
                    ? (data.lines[0] ?? line)
                    : line
                ),
              }
            : data
        )
        return invalidatePlan(client, id, sameKey(keys.lines(id)))
      },
    }
  )
}

export function useResetWavePlanLines(id: string) {
  const client = useQueryClient()
  return usePlanMutation(
    id,
    ({ lineRefId }: { lineRefId?: string }) => {
      const version = latestVersion(client, id)
      return lineRefId
        ? resetWavePlanLine(id, lineRefId, version)
        : resetWavePlanLines(id, version)
    },
    {
      error: 'Could not reset SKU changes',
      success: variables =>
        variables.lineRefId
          ? 'SKU reset to the overall plan'
          : 'SKU changes reset',
    }
  )
}

/** Edits of the selected SKUs: one request, one undo step. */
export type WavePlanSkuBulkEdit =
  | { kind: 'set'; lineRefIds: string[]; weekId: string; percent: number }
  | { kind: 'reset'; lineRefIds: string[] }

type SkuBulkOutcome = WavePlanSkuBulkResponse & {
  /** SKUs the request asked to change (unchanged ones are not sent). */
  sent: string[]
}

/**
 * Each selected SKU gets the edit computed from its own cells (the same rules
 * as a single edit); SKUs it would leave as they are are not sent. The toast
 * counts, from the refreshed SKU view, the changed SKUs that don't add up.
 */
export function useBulkWavePlanLines(id: string) {
  const client = useQueryClient()
  return usePlanMutation(
    id,
    async (edit: WavePlanSkuBulkEdit): Promise<SkuBulkOutcome> => {
      const view = cachedView<WavePlanLinesResponse>(
        client,
        keys.lines(id),
        'SKU detail'
      )
      const inView = new Set(view.lines.map(line => line.line_ref_id))
      const refs = edit.lineRefIds.filter(ref => inView.has(ref))
      let body: WavePlanSkuBulkRequest
      let sent: string[]
      if (edit.kind === 'reset') {
        body = { op: 'reset', line_ref_ids: refs }
        sent = refs
      } else {
        const lines = refs
          .map(ref => ({
            line_ref_id: ref,
            weeks: skuWeeksWithEdit(view, ref, edit.weekId, edit.percent),
          }))
          .filter(
            line => !skuRequestUnchanged(view, line.line_ref_id, line.weeks)
          )
        body = { op: 'set_weeks', lines }
        sent = lines.map(line => line.line_ref_id)
      }
      if (!sent.length)
        return {
          inputs_version: view.inputs_version,
          applied: 0,
          skipped: [],
          sent,
        }
      const result = await bulkWavePlanLines(id, body, view.inputs_version)
      return { ...result, sent }
    },
    {
      error: 'Could not update the selected SKUs',
      onSuccess: async (client, data, variables) => {
        if (!data.sent.length) {
          toast.success('The selected SKUs already have these values')
          return
        }
        await invalidatePlan(client, id)
        const skipped = new Set(data.skipped.map(item => item.line_ref_id))
        const changed = new Set(data.sent.filter(ref => !skipped.has(ref)))
        const count = (n: number) => `${n} ${n === 1 ? 'SKU' : 'SKUs'}`
        const done =
          variables.kind === 'reset'
            ? `Reset ${count(data.applied)} to the plan`
            : `Updated ${count(data.applied)}`
        if (!isFresh(client, keys.lines(id))) {
          // The edit is saved, but its result couldn't be reloaded to count it.
          toast.warning(
            `${done}${skipped.size > 0 ? ` · skipped ${skipped.size}` : ''}. The SKU detail couldn't be reloaded: refresh the page to see the result.`
          )
          return
        }
        const lines =
          client.getQueryData<WavePlanLinesResponse>(keys.lines(id))?.lines ??
          []
        const unbalanced = lines.filter(
          line =>
            changed.has(line.line_ref_id) &&
            !samePercent(line.total_percent, 100)
        ).length
        const parts = [
          done,
          unbalanced > 0 &&
            `${unbalanced} ${unbalanced === 1 ? "doesn't" : "don't"} add up to 100%`,
          skipped.size > 0 && `skipped ${skipped.size}`,
        ].filter(Boolean)
        const message = parts.join(' · ')
        if (unbalanced > 0 || skipped.size > 0) toast.warning(message)
        else toast.success(message)
      },
    }
  )
}

/**
 * The current user's undoable edits of the plan, most recent first. ``fresh``:
 * loaded, idle (not fetching or paused offline) and not invalidated, so the top
 * entry is the edit an undo reverts.
 */
export function useWavePlanUndoStack(id: string, enabled = true) {
  const client = useQueryClient()
  const query = useWaveQuery({
    queryKey: undoStackKey(id),
    queryFn: () => fetchWavePlanUndoStack(id),
    enabled: !!id && enabled,
    errorMessage: 'Could not load your undo history',
  })
  // Reading status and fetchStatus subscribes to every change that matters.
  const fresh =
    query.status === 'success' &&
    query.fetchStatus === 'idle' &&
    isFresh(client, undoStackKey(id))
  return { ...query, fresh }
}

/**
 * Undo the edit the user confirmed (``entryId``). Like the AP's undo, the toast
 * shows the undone edit's description; a conflict (the same inputs changed
 * since) or a newer edit refreshes the history.
 */
export function useUndoWavePlan(id: string) {
  const client = useQueryClient()
  return usePlanMutation(
    id,
    async (entryId: string) => {
      try {
        return await undoWavePlanChange(id, entryId, latestVersion(client, id))
      } catch (error) {
        if (errorStatus(error) === 404) throw new Error('Nothing to undo.')
        throw error
      }
    },
    {
      error: 'Failed to undo change',
      onSuccess: async (client, data) => {
        toast.success(data.description || 'Change undone')
        await invalidatePlan(client, id)
      },
      onError: client =>
        void client.invalidateQueries({ queryKey: undoStackKey(id) }),
    }
  )
}

export function useUpdateWavePlanDcSplit(id: string) {
  const client = useQueryClient()
  return usePlanMutation(
    id,
    ({
      lineRefId,
      weekId,
      dcId,
      percent,
    }: {
      lineRefId: string
      weekId: string
      dcId: string
      percent: number
    }) => {
      const view = cachedDcView(client, id, weekId)
      return updateWavePlanDcSplit(
        id,
        lineRefId,
        weekId,
        dcSplitWithEdit(view, lineRefId, dcId, percent),
        view.inputs_version
      )
    },
    {
      error: 'Could not update the DC split',
      onSuccess: (client, data) => {
        // Every cached DC view of the edited week (by id or as "first") is current.
        const showsWeek = (query: Query) =>
          (query.state.data as WavePlanDcResponse | undefined)?.week?.id ===
          data.week?.id
        client.setQueryData(keys.dc(id, data.week?.id), data)
        for (const query of client
          .getQueryCache()
          .findAll({ queryKey: keys.dcViews(id) }))
          if (showsWeek(query)) client.setQueryData(query.queryKey, data)
        return invalidatePlan(client, id, showsWeek)
      },
    }
  )
}

/**
 * ``inputsVersion`` pins the edit to what the user saw (the copy dialog's
 * draft); without it, ``set_dc_percent`` uses the freshest view of its week.
 */
export function useBulkWavePlanDc(id: string) {
  const client = useQueryClient()
  return usePlanMutation(
    id,
    ({
      body,
      inputsVersion,
    }: {
      body: WavePlanDcBulkRequest
      inputsVersion?: number
    }) =>
      bulkWavePlanDcSplits(
        id,
        body,
        inputsVersion ??
          (body.op === 'set_dc_percent'
            ? cachedDcView(client, id, body.week_id).inputs_version
            : latestVersion(client, id))
      ),
    { error: 'Could not update DC splits' }
  )
}

export function useResetWavePlanDc(id: string) {
  const client = useQueryClient()
  return usePlanMutation(
    id,
    () => resetWavePlanDcSplits(id, latestVersion(client, id)),
    { error: 'Could not reset DC changes', success: 'DC changes reset' }
  )
}

export function useRefreshWavePlan(id: string) {
  return usePlanMutation(
    id,
    (inputsVersion: number) => refreshWavePlan(id, inputsVersion),
    {
      error: 'Could not refresh the wave plan',
      success: 'Wave plan refreshed from the assortment plan',
    }
  )
}

export function useSubmitWavePlan(id: string) {
  return usePlanMutation(
    id,
    (body: { reviewer_ids: string[]; notes?: string | null }) =>
      submitWavePlan(id, body),
    {
      error: 'Could not submit the wave plan',
      success: 'Wave plan submitted for approval',
    }
  )
}

export function useWithdrawWavePlan(id: string) {
  return usePlanMutation(
    id,
    (submissionId: string) => withdrawWavePlanSubmission(id, submissionId),
    {
      error: 'Could not withdraw the submission',
      success: 'Submission withdrawn',
    }
  )
}

const DECISION_MESSAGES: Record<WavePlanDecision, string> = {
  approve: 'Wave plan approved',
  'send-back': 'Wave plan sent back',
  unapprove: 'Wave plan reopened',
}

export function useDecideWavePlan(id: string) {
  return usePlanMutation(
    id,
    ({
      submissionId,
      decision,
      notes,
    }: {
      submissionId: string
      decision: WavePlanDecision
      notes?: string | null
    }) => decideWavePlanSubmission(submissionId, decision, notes),
    {
      error: 'Could not record the decision',
      success: variables => DECISION_MESSAGES[variables.decision],
    }
  )
}

export function useCreateWavePlanLineComment(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      lineRefId,
      content,
    }: {
      lineRefId: string
      content: string
    }) => createWavePlanLineComment(id, lineRefId, content),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({
        queryKey: keys.lineComments(id, variables.lineRefId),
      })
      void client.invalidateQueries({ queryKey: keys.lines(id) })
    },
    onError: (error: unknown) =>
      toast.error(getErrorMessage(error, 'Could not post the comment')),
  })
}

export function useDeleteWavePlanLineComment(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ commentId }: { commentId: string; lineRefId: string }) =>
      deleteWavePlanComment(commentId),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({
        queryKey: keys.lineComments(id, variables.lineRefId),
      })
      void client.invalidateQueries({ queryKey: keys.lines(id) })
    },
    onError: (error: unknown) =>
      toast.error(getErrorMessage(error, 'Could not delete the comment')),
  })
}
